// Вкладка «Поля карточек» раздела «Учётки и доступ» (не самостоятельный экран: шапку
// с <h1> и маршрут держит pages/AdminUsers.tsx — потому файл в components/, а не в
// pages/, как AdminNabory). Здесь владелец сам добавляет, правит и архивирует поля
// карточек сделок/компаний/контактов/судов — без программиста и выкладки.
//
// ДВА ПРАВИЛА, КОТОРЫЕ ЭКРАН ГОВОРИТ ВСЛУХ:
//   • поле появляется на карточках сразу; значения хранятся в CRM и в Bitrix
//     НЕ ПИШУТСЯ — это своя схема ядра, а не зеркало;
//   • удаления нет — только архив: поле исчезает с карточек, данные не стираются,
//     вернуть его можно в любой момент.
//
// Системные поля (из спеки карточки, builtin) показаны серым «встроенное» и не
// правятся: их состав держит спека, а не этот экран. Код существующего поля не
// меняется — к нему привязаны накопленные значения.

import { useState } from 'react'

import type { ОбзорПолей, ПолеКарточки, Сущность, ТипПоля } from '../api/cardFields'
import {
  КОД_ПОЛЯ, кодИзНазвания, СУЩНОСТИ, ТИПЫ, useАрхивПоля, useПоляКарточек,
  useСохранениеПоля,
} from '../api/cardFields'
import { QueryState } from './QueryState'

export default function AdminCardFields() {
  const [сущность, setСущность] = useState<Сущность>('сделка')
  const запрос = useПоляКарточек(сущность)
  const д = запрос.data
  const [новое, setНовое] = useState(false)
  const [правлю, setПравлю] = useState('')   // code поля с раскрытым редактором
  const [архивДля, setАрхивДля] = useState('') // code поля с раскрытым подтверждением

  const закрытьВсё = () => { setНовое(false); setПравлю(''); setАрхивДля('') }

  return (
    <>
      <div className="notice" style={{ marginBottom: 16 }}>
        Поле появится на карточках сразу; значения хранятся в CRM и в Bitrix не
        пишутся. Удаления полей нет — только архив: поле уходит с карточек,
        а накопленные данные не стираются.
      </div>

      <div className="page-head" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {СУЩНОСТИ.map((с) => (
            <button key={с.код} onClick={() => { setСущность(с.код); закрытьВсё() }}
                    aria-pressed={сущность === с.код}
                    style={{ fontWeight: сущность === с.код ? 600 : 400 }}>
              {с.имя}
            </button>
          ))}
        </div>
        <span className="spacer" />
        <button onClick={() => { setНовое(!новое); setПравлю(''); setАрхивДля('') }}>
          {новое ? 'закрыть' : 'новое поле'}
        </button>
      </div>

      <QueryState isLoading={запрос.isLoading} error={запрос.error}>
        {новое && д && (
          <РедакторПоля сущность={сущность} исходное={null} обзор={д}
                        готово={() => setНовое(false)} />
        )}
        <div className="card">
          <div className="table-wrap cards">
            <table>
              <thead>
                <tr>
                  <th>Поле</th>
                  <th className="nowrap">Код</th>
                  <th className="nowrap">Тип</th>
                  <th className="nowrap">Группа</th>
                  <th className="nowrap">Обязательное</th>
                  <th className="nowrap">Порядок</th>
                  <th className="nowrap">Состояние</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {(д?.fields ?? []).map((п) => (
                  <СтрокаПоля key={п.code} п={п} обзор={д!}
                              правлюЯ={правлю === п.code}
                              архивОткрыт={архивДля === п.code}
                              наПравку={() => { setПравлю(правлю === п.code ? '' : п.code); setНовое(false); setАрхивДля('') }}
                              наАрхив={() => { setАрхивДля(архивДля === п.code ? '' : п.code); setПравлю(''); setНовое(false) }} />
                ))}
                {д && д.fields.length === 0 && (
                  <tr>
                    <td colSpan={8} className="empty">
                      <div className="empty-title">Полей нет</div>
                      <div className="empty-hint">
                        Ядро не отдало ни системных, ни владельческих полей —
                        так не бывает: у карточки всегда есть встроенные поля.
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </QueryState>
    </>
  )
}

/** Подпись типа — человеку («список»), а не программисту ('list'). */
function имяТипа(т: ТипПоля): string {
  return ТИПЫ.find((x) => x.код === т)?.имя ?? т
}

function имяГруппы(обзор: ОбзорПолей, код: string): string {
  return обзор.groups?.find((г) => г.code === код)?.title ?? код
}

/** Соседи по перестановке: НЕархивные поля владельца той же группы, по порядку. */
function соседи(обзор: ОбзорПолей, п: ПолеКарточки): ПолеКарточки[] {
  return обзор.fields
    .filter((x) => !x.builtin && !x.archived && x.group === п.group)
    .sort((a, b) => a.order - b.order || (a.code < b.code ? -1 : 1))
}

function СтрокаПоля({ п, обзор, правлюЯ, архивОткрыт, наПравку, наАрхив }: {
  п: ПолеКарточки
  обзор: ОбзорПолей
  правлюЯ: boolean
  архивОткрыт: boolean
  наПравку: () => void
  наАрхив: () => void
}) {
  const сохранение = useСохранениеПоля()
  const ряд = соседи(обзор, п)
  const место = ряд.findIndex((x) => x.code === п.code)

  /** Стрелка: обмен order с соседом — два сохранения, у каждого полное тело поля. */
  const поменять = (сосед: ПолеКарточки) => {
    const тело = (x: ПолеКарточки, order: number) => ({
      entity: x.entity, code: x.code, title: x.title, type: x.type,
      ...(x.type === 'list' ? { options: x.options ?? [] } : {}),
      group: x.group, required: x.required, order,
    })
    сохранение.mutate(тело(п, сосед.order))
    сохранение.mutate(тело(сосед, п.order))
  }

  return (
    <>
      <tr>
        <td data-label="Поле"><b>{п.title}</b></td>
        <td data-label="Код" className="nowrap"><span className="mono">{п.code}</span></td>
        <td data-label="Тип" className="nowrap">
          {имяТипа(п.type)}
          {п.type === 'list' && (п.options?.length ?? 0) > 0 && (
            <div className="hint">{п.options!.join(' · ')}</div>
          )}
        </td>
        <td data-label="Группа" className="nowrap">{имяГруппы(обзор, п.group)}</td>
        <td data-label="Обязательное" className="nowrap">{п.required ? 'да' : '—'}</td>
        <td data-label="Порядок" className="nowrap">
          {п.builtin || п.archived ? (
            <span className="muted">{п.order}</span>
          ) : (
            <>
              <button title="выше на карточке" disabled={место <= 0 || сохранение.isPending}
                      onClick={() => поменять(ряд[место - 1])}>↑</button>{' '}
              <button title="ниже на карточке"
                      disabled={место < 0 || место >= ряд.length - 1 || сохранение.isPending}
                      onClick={() => поменять(ряд[место + 1])}>↓</button>
            </>
          )}
        </td>
        <td data-label="Состояние" className="nowrap">
          {п.builtin
            ? <span className="muted">встроенное</span>
            : п.archived
              ? <span className="badge">в архиве</span>
              : <span className="badge won">на карточке</span>}
        </td>
        <td data-label="Действия" className="nowrap">
          {/* Системное поле не правится и не архивируется: его держит спека карточки. */}
          {!п.builtin && !п.archived && (
            <>
              <button onClick={наПравку}>{правлюЯ ? 'свернуть' : 'править'}</button>{' '}
              <button onClick={наАрхив}>в архив</button>
            </>
          )}
          {!п.builtin && п.archived && <КнопкаВозврата п={п} />}
        </td>
      </tr>
      {сохранение.error && (
        <tr><td colSpan={8} className="error">{(сохранение.error as Error).message}</td></tr>
      )}
      {правлюЯ && (
        <tr><td colSpan={8}>
          <РедакторПоля сущность={п.entity} исходное={п} обзор={обзор} готово={наПравку} />
        </td></tr>
      )}
      {архивОткрыт && (
        <tr><td colSpan={8}><ПодтверждениеАрхива п={п} готово={наАрхив} /></td></tr>
      )}
    </>
  )
}

function КнопкаВозврата({ п }: { п: ПолеКарточки }) {
  const архив = useАрхивПоля()
  return (
    <>
      <button disabled={архив.isPending}
              title="Поле снова появится на карточках; данные всё это время не стирались"
              onClick={() => архив.mutate({ entity: п.entity, code: п.code, archived: false })}>
        вернуть из архива
      </button>
      {архив.error && <div className="error">{(архив.error as Error).message}</div>}
    </>
  )
}

/** Архив — с подтверждением и объяснением: это не удаление, данные не стираются. */
function ПодтверждениеАрхива({ п, готово }: { п: ПолеКарточки; готово: () => void }) {
  const архив = useАрхивПоля()
  return (
    <div className="card" data-confirm={п.code}>
      <b>Поле «{п.title}» — в архив?</b>
      <div className="hint" style={{ margin: '4px 0 8px' }}>
        Поле уйдёт с карточек, но данные не стираются: всё, что люди в него вписали,
        остаётся в CRM, и поле можно вернуть из архива в любой момент. Удаления полей
        здесь нет вовсе.
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <button disabled={архив.isPending}
                onClick={() => архив.mutate({ entity: п.entity, code: п.code, archived: true },
                                            { onSuccess: готово })}>
          в архив
        </button>
        <button onClick={готово} disabled={архив.isPending}>отмена</button>
      </div>
      {архив.error && (
        <div className="error" style={{ marginTop: 8 }}>{(архив.error as Error).message}</div>
      )}
    </div>
  )
}

/**
 * Редактор поля: добавление и правка — одна форма. Код у нового поля подставляется
 * транслитом из названия (пока человек не поправил его руками); у существующего код
 * закрыт — к нему привязаны накопленные значения.
 */
function РедакторПоля({ сущность, исходное, обзор, готово }: {
  сущность: Сущность
  исходное: ПолеКарточки | null
  обзор: ОбзорПолей
  готово: () => void
}) {
  const сохранение = useСохранениеПоля()
  const [название, setНазвание] = useState(исходное?.title ?? '')
  const [код, setКод] = useState(исходное?.code ?? '')
  const [кодРуками, setКодРуками] = useState(исходное !== null)
  const [тип, setТип] = useState<ТипПоля>(исходное?.type ?? 'text')
  const [варианты, setВарианты] = useState((исходное?.options ?? []).join('\n'))
  const [группа, setГруппа] = useState(
    исходное?.group ?? (обзор.groups?.[0]?.code ?? ''))
  const [обязательное, setОбязательное] = useState(исходное?.required ?? false)
  const [ошибка, setОшибка] = useState('')

  const сменитьНазвание = (v: string) => {
    setНазвание(v)
    if (!кодРуками) setКод(кодИзНазвания(v))
  }

  const сохранить = () => {
    setОшибка('')
    if (!название.trim()) {
      setОшибка('Название пустое.')
      return
    }
    const к = код.trim().toLowerCase()
    if (!КОД_ПОЛЯ.test(к)) {
      setОшибка('Код поля — слово латиницей: строчные буквы, цифры и подчёркивание, '
        + 'первая — буква (например port_pripiski).')
      return
    }
    if (исходное === null && обзор.fields.some((п) => п.code === к)) {
      const занял = обзор.fields.find((п) => п.code === к)!
      setОшибка(занял.builtin
        ? `Код «${к}» занят встроенным полем «${занял.title}» — придумайте другой.`
        : `Код «${к}» уже занят полем «${занял.title}».`)
      return
    }
    if (!группа.trim()) {
      setОшибка('Не выбрана группа: без неё непонятно, куда поле встаёт на карточке.')
      return
    }
    const строки = варианты.split('\n').map((s) => s.trim()).filter(Boolean)
    if (тип === 'list' && строки.length === 0) {
      setОшибка('У списка нужны варианты — по одному в строке.')
      return
    }
    // Порядок нового поля — за последним полем владельца (или за встроенными).
    const order = исходное?.order
      ?? Math.max(0, ...обзор.fields.map((п) => п.order)) + 1
    сохранение.mutate({
      entity: сущность, code: к, title: название.trim(), type: тип,
      ...(тип === 'list' ? { options: строки } : {}),
      group: группа.trim(), required: обязательное, order,
    }, { onSuccess: готово })
  }

  return (
    <div className="card" data-editor={исходное?.code ?? 'новое'}
         style={{ marginBottom: 16 }}>
      <b>{исходное ? `Правка поля «${исходное.title}»` : 'Новое поле'}</b>
      <div className="hint" style={{ margin: '4px 0 8px' }}>
        {исходное
          ? 'Код поля не меняется: к нему привязаны накопленные значения.'
          : 'Код подставится из названия сам; поправить его можно только до сохранения.'}
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <label>Название{' '}
          <input value={название} onChange={(e) => сменитьНазвание(e.target.value)}
                 placeholder="Порт приписки" />
        </label>
        <label>Код{' '}
          <input value={код} disabled={исходное !== null} className="mono"
                 onChange={(e) => { setКод(e.target.value); setКодРуками(true) }}
                 placeholder="port_pripiski" style={{ width: 150 }} />
        </label>
        <label>Тип{' '}
          <select value={тип} disabled={исходное !== null}
                  title={исходное !== null
                    ? 'Тип не меняется: накопленные значения перестали бы читаться'
                    : undefined}
                  onChange={(e) => setТип(e.target.value as ТипПоля)}>
            {ТИПЫ.map((т) => <option key={т.код} value={т.код}>{т.имя}</option>)}
          </select>
        </label>
        <label>Группа{' '}
          {(обзор.groups?.length ?? 0) > 0 ? (
            <select value={группа} onChange={(e) => setГруппа(e.target.value)}>
              {обзор.groups!.map((г) => (
                <option key={г.code} value={г.code}>{г.title}</option>
              ))}
            </select>
          ) : (
            <input value={группа} onChange={(e) => setГруппа(e.target.value)}
                   placeholder="код группы" style={{ width: 130 }} />
          )}
        </label>
        <label className="nowrap" style={{ fontWeight: 400 }}>
          <input type="checkbox" checked={обязательное}
                 onChange={(e) => setОбязательное(e.target.checked)} />{' '}
          обязательное
        </label>
      </div>
      {тип === 'list' && (
        <label style={{ display: 'block', marginTop: 8 }}>
          Варианты списка — по одному в строке{' '}
          <textarea value={варианты} onChange={(e) => setВарианты(e.target.value)}
                    rows={4} style={{ display: 'block', width: '100%', marginTop: 4 }}
                    placeholder={'Санкт-Петербург\nМурманск'} />
        </label>
      )}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8 }}>
        <button onClick={сохранить} disabled={сохранение.isPending}>сохранить</button>
        <button onClick={готово} disabled={сохранение.isPending}>отмена</button>
      </div>
      {(ошибка || сохранение.error) && (
        <div className="error" style={{ marginTop: 8 }}>
          {ошибка || (сохранение.error as Error).message}
        </div>
      )}
    </div>
  )
}
